import React, {useEffect, useMemo} from 'react';
import type {SetOptional} from 'type-fest';
import {getAttributeLabelFromId} from '@netx/core-asset/components/Attributes/Attribute/utils';
import type {InputForm} from '@netx/core-components/Form/typings';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	SelectField,
	type SelectFieldProps,
} from '@netx/core-components/SelectField';
import {splitEventHandler} from '@netx/core-next/utils';
import {i18n} from '@netx/core/i18n';
import {type IAttributeTemplate} from '../../schema';

export interface AttributeDropdownProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
> extends SetOptional<
		SelectFieldProps<Option, Multiple, DisableClearable, false>,
		'name' | 'options'
	> {
	attributes: IAttributeTemplate[];
	selected?: number[];
	form?: InputForm;
	readOnly?: boolean;
}

const getOptionSelected = (option: Option, value: Option) =>
	option?.id === value?.id;

const getOptionLabel = (option: Option) => option.value;

type Option = {
	value: string;
	id: number;
};

const DEFAULT_SELECTED: number[] = [];

export const AttributeDropdownPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	{
		attributes,
		readOnly,
		form,
		name = 'attribute-select',
		selected = DEFAULT_SELECTED,
		filterOptions,
		onChange,
		...props
	}: AttributeDropdownProps<Multiple, DisableClearable>,
	ref: any,
) => {
	const options = useMemo<Option[]>(() => {
		return attributes
			.filter(({attributeid}) => !selected.includes(attributeid))
			.map(({attributeid}) => ({
				value: getAttributeLabelFromId(attributeid),
				id: attributeid,
			}));
	}, [attributes, selected]);

	useEffect(() => {
		if (!form) return;
		form.register(name);
		return () => {
			form.unregister(name);
		};
	}, [name]);

	const handleChange = useConstCallback(
		splitEventHandler(onChange, (event, value) => {
			form?.setValue(name, value);
		}),
	);

	return (
		<SelectField<Option, Multiple, DisableClearable>
			ref={ref}
			readOnly={readOnly}
			label={i18n.t('i18n.attributes')}
			{...props}
			name={name}
			options={options}
			getOptionLabel={getOptionLabel}
			filterOptions={(options, state) => {
				const inputValue = state.inputValue.toLowerCase();
				const filteredOptions =
					inputValue === ''
						? options
						: options.filter((option) =>
								option.value.toLowerCase().includes(inputValue),
						  );

				return filterOptions
					? filterOptions(filteredOptions, state)
					: filteredOptions;
			}}
			isOptionEqualToValue={getOptionSelected}
			onChange={handleChange}
			InputProps={{
				...props.InputProps,
				InputProps: {
					...props.InputProps?.InputProps,
					style: {
						cursor: props.disabled ? 'not-allowed' : 'pointer',
						...props.InputProps?.InputProps?.style,
					},
				},
				inputProps: {
					'aria-label': 'attribute select field',
					placeholder: i18n.t('i18n.pleaseSelect'),
					...props.InputProps?.inputProps,
					style: {
						cursor: props.disabled ? 'not-allowed' : 'pointer',
						...props.InputProps?.inputProps?.style,
					},
				},
			}}
		/>
	);
};

export const AttributeDropdown = React.forwardRef(
	AttributeDropdownPrimitive,
) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: AttributeDropdownProps<Multiple, DisableClearable>,
) => JSX.Element;

export default AttributeDropdown;
